import { AxiosError } from 'axios';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { URL_EXTENSION } from '@utils/constants/app-constants';
import { BaseError } from '@utils/types/shared/base';
import {
  OneOffChargeResponse,
  OneOffChargesResponse,
  CreateOneOffChargePayload
} from '@utils/types/back-office/usage-revenue';

const ONE_OFF_CHARGES_ENDPOINT = `${URL_EXTENSION}/admin/billing/one-off-charges`;

/**
 * Hook to fetch one-off charges
 *
 * GET /api/admin/billing/one-off-charges
 * Query params:
 *   - organization_id, billing_period_id, invoiced
 *   - q: search by organization name
 *   - description_q: search by description
 *   - created_by_q: search by creator
 *   - min_amount: minimum amount filter
 *   - sort: organization_name | description | amount_excl | created_at | created_by
 *   - direction: asc | desc
 *   - limit, offset
 */
export const useGetOneOffCharges = (options?: {
  organizationId?: number;
  billingPeriodId?: number;
  invoiced?: boolean;
  sort?: string;
  direction?: 'asc' | 'desc';
  search?: string;
  descriptionQ?: string;
  createdByQ?: string;
  minAmount?: number;
  limit?: number;
  offset?: number;
}) => {
  const {
    organizationId,
    billingPeriodId,
    invoiced,
    sort = 'created_at',
    direction = 'desc',
    search,
    descriptionQ,
    createdByQ,
    minAmount,
    limit = 25,
    offset = 0
  } = options || {};

  const fetchOneOffCharges = async () => {
    const params = new URLSearchParams();

    if (organizationId != null)
      params.append('organization_id', String(organizationId));
    if (billingPeriodId != null)
      params.append('billing_period_id', String(billingPeriodId));
    if (invoiced != null) params.append('invoiced', String(invoiced));
    if (sort) params.append('sort', sort);
    if (direction) params.append('direction', direction);
    if (search) params.append('q', search);
    if (descriptionQ) params.append('description_q', descriptionQ);
    if (createdByQ) params.append('created_by_q', createdByQ);
    if (minAmount != null) params.append('min_amount', String(minAmount));
    params.append('limit', String(limit));
    params.append('offset', String(offset));

    const response = await Axios.get<OneOffChargesResponse>(
      `${ONE_OFF_CHARGES_ENDPOINT}?${params.toString()}`
    );
    return response.data;
  };

  return useQuery<OneOffChargesResponse, AxiosError<BaseError>>(
    [
      'oneOffCharges',
      organizationId,
      billingPeriodId,
      invoiced,
      sort,
      direction,
      search,
      descriptionQ,
      createdByQ,
      minAmount,
      limit,
      offset
    ],
    fetchOneOffCharges,
    {
      useErrorBoundary: false,
      keepPreviousData: true
    }
  );
};

/**
 * Hook to create a one-off charge
 *
 * POST /api/admin/billing/one-off-charges
 * Request body:
 *   - organization_id (required)
 *   - billing_period_id (optional, nullable)
 *   - description (required)
 *   - amount_excl (required)
 */
export const useCreateOneOffCharge = () => {
  const queryClient = useQueryClient();

  return useMutation<
    OneOffChargeResponse,
    AxiosError<BaseError>,
    CreateOneOffChargePayload
  >(
    async payload => {
      const response = await Axios.post<OneOffChargeResponse>(
        ONE_OFF_CHARGES_ENDPOINT,
        payload
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['oneOffCharges']);
        queryClient.invalidateQueries(['billables']);
      }
    }
  );
};

/**
 * Hook to delete a one-off charge
 *
 * DELETE /api/admin/billing/one-off-charges/{charge_id}
 * Only works if not yet invoiced (no invoice_line_id)
 * Returns { "ok": true }
 */
export const useDeleteOneOffCharge = () => {
  const queryClient = useQueryClient();

  return useMutation<{ ok: boolean }, AxiosError<BaseError>, number>(
    async chargeId => {
      const response = await Axios.delete<{ ok: boolean }>(
        `${ONE_OFF_CHARGES_ENDPOINT}/${chargeId}`
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['oneOffCharges']);
        queryClient.invalidateQueries(['billables']);
      }
    }
  );
};

// Legacy exports for backward compatibility (deprecated)
/** @deprecated Use useGetOneOffCharges instead */
export const useGetOneOffLines = useGetOneOffCharges;
/** @deprecated Use useCreateOneOffCharge instead */
export const useCreateOneOffLine = useCreateOneOffCharge;
/** @deprecated Use useDeleteOneOffCharge instead */
export const useDeleteOneOffLine = useDeleteOneOffCharge;
